import { PartnerAffiliation, PartnerProduct } from '@/@types/PartnerProgram';
import Loader from '@/components/Loader/Loader';
import { ProductsGrid } from '@/components/shared/ProductsGrid/ProductsGrid';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { PartnerLinksService } from '@/services/partner-links.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Link as LinkIcon, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  products: PartnerProduct[];
  isLoading: boolean;
}

export default function ReferralLinksGrid({ products, isLoading }: Props) {
  const { t } = useTranslation(['affiliates', 'common', 'invitationAffiliateLink']);
  const [selectedProduct, setSelectedProduct] = useState<PartnerProduct | null>(
    null,
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  const gridProducts = products.map((p) => ({
    id: p.id,
    name: p.name,
    images: p.image ? [{ url: p.image }] : [],
    status: 'active',
  }));

  return (
    <>
      <ProductsGrid
        products={gridProducts}
        onProductClick={(product) => {
          const original = products.find((p) => p.id === product.id);
          if (original) setSelectedProduct(original);
        }}
        actionLabel={t('affiliates:view_links')}
      />

      {selectedProduct && (
        <ReferralLinksPanel
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </>
  );
}

function ReferralLinksPanel({
  product,
  onClose,
}: {
  product: PartnerProduct;
  onClose: () => void;
}) {
  const { t } = useTranslation(['affiliates', 'common', 'invitationAffiliateLink']);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['partner-links', product.id],
    queryFn: () => PartnerLinksService.getPartnerLinks(product.id),
    staleTime: 10 * 1000,
  });

  const { mutate: generateLink, isPending: isGenerating } = useMutation({
    mutationFn: () => PartnerLinksService.generatePartnerLink(product.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-links', product.id] });
      toast({
        variant: 'success',
        description: t('affiliates:partner_link_created'),
      });
    },
    onError: () => {
      toast({
        variant: 'error',
        description: t('invitationAffiliateLink:error'),
      });
    },
  });

  const links = data?.data ?? [];

  const handleCopyLink = async (aff: PartnerAffiliation) => {
    if (!aff.invite_code) return;

    try {
      const origin = window.location.origin;
      const fullLink = `${origin}/invitation-affiliate/${product.id}?partner_invite_code=${aff.invite_code}`;
      await navigator.clipboard.writeText(fullLink);
      toast({
        variant: 'success',
        description: t('common:linkCopied'),
      });
    } catch {
      toast({
        variant: 'error',
        description: t('common:link_copy_error'),
      });
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[95vw] sm:w-[90vw] max-w-3xl max-h-[85vh] overflow-hidden p-0 rounded-lg">
        <div className="flex flex-col max-h-[85vh] min-w-0">
          <DialogHeader className="p-4 sm:p-6 pb-3 sm:pb-4 border-b border-colorBorder shrink-0 text-left">
            <div className="flex items-center gap-3 pr-6">
              <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary/10 border border-primary/20 shrink-0">
                <LinkIcon size={16} className="text-primary sm:w-5 sm:h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-sm sm:text-xl font-semibold truncate">
                    {product.name}
                  </DialogTitle>
                  {links.length > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 text-[10px] sm:text-xs font-bold rounded-full bg-primary/15 text-primary border border-primary/30 shrink-0">
                      {links.length}
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground text-[10px] sm:text-sm mt-0.5 truncate">
                  {t('affiliates:referral_links_title')}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-4 min-w-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader />
              </div>
            ) : links.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-center space-y-3">
                <LinkIcon className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {t('affiliates:no_links_yet')}
                </p>
                <Button
                  size="sm"
                  onClick={() => generateLink()}
                  disabled={isGenerating}
                  data-cy="btn-generate-partner-link"
                >
                  {isGenerating ? (
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  ) : null}
                  {t('affiliates:generate_link')}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {links.map((aff) => (
                  <div
                    key={aff.id}
                    className="flex items-center gap-3 p-3 border border-border rounded-lg hover:bg-zinc-900/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0 space-y-1">
                      {aff.invite_code && (
                        <p className="text-xs text-muted-foreground font-mono truncate">
                          {`${window.location.origin}/invitation-affiliate/${product.id}?partner_invite_code=${aff.invite_code}`}
                        </p>
                      )}
                    </div>
                    {aff.invite_code && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyLink(aff)}
                        className="flex-shrink-0"
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        {t('common:buttons.copy')}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
